import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GamesGroupQuery,
  GroupType,
  IGroupSummary,
  PredictorGroupSummary,
  PredictorProcessStatus,
  ProgressStatus,
  SortFn,
} from '@fgb/core';
import {
  FilterFn,
  FilterLogicalOperator,
  filterMultiple,
  sortMultiple,
} from '@fgb/portal-component-library/src/lib/shared/utilities';
import { DateTime } from 'luxon';
import { BehaviorSubject, map, merge } from 'rxjs';

@Component({
  selector: 'fgbcl-games-group-list',
  templateUrl: './games-group-list.component.html',
  styleUrl: './games-group-list.component.scss',
})
export class GamesGroupListComponent implements OnInit {
  @Input() gamesGroup: IGroupSummary[] = [];
  @Input() groupType: GroupType;
  @Input() set filters(filters: FilterFn[] | FilterLogicalOperator[]) {
    this.filtersSubject.next(filters || []);
  }
  @Input() set sort(sort: SortFn[]) {
    this.sortSubject.next(sort);
  }
  // Toggle to use default behavior or use filters and sort
  // false will use the provided filters and sort
  // true will use inner sort and filter logic
  @Input() liveUpcomingTab: boolean = true;
  @Input() default: boolean = true;
  groupTypes = GroupType;
  private dataChangeSubject = new BehaviorSubject<IGroupSummary[]>([]);
  private filtersSubject = new BehaviorSubject<FilterFn[] | FilterLogicalOperator[]>([]);
  private sortSubject = new BehaviorSubject<SortFn[]>([]);
  private destroyRef = inject(DestroyRef);

  constructor(public gamesGroupQuery: GamesGroupQuery) {}

  ngOnInit(): void {
    // Workaround
    if (!this.default) {
      this.dataChangeSubject.next(this.gamesGroup);
      merge(this.dataChange$, this.filterChange$, this.sortChange$)
        .pipe(
          map(() =>
            this.dataChangeSubject
              .getValue()
              .sort(sortMultiple(...this.sortSubject.getValue()))
              .filter(filterMultiple(...this.filtersSubject.getValue()))
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((filteredGamesGroup) => {
          this.gamesGroup = filteredGamesGroup;
        });
      return;
    }

    this.hideEndedPredictorGamesGroup();
    this.sortGameGroups();
    this.checkComingSoon();
  }

  get dataChange$() {
    return this.dataChangeSubject.asObservable();
  }

  get sortChange$() {
    return this.sortSubject.asObservable();
  }

  get filterChange$() {
    return this.filtersSubject.asObservable();
  }

  checkComingSoon(event?: any) {
    if (!this.default) {
      if (event?.completed) {
        const updatedIndex = this.dataChangeSubject.getValue().findIndex((g) => g.GroupId === event?.groupId);
        if (updatedIndex !== -1) {
          let data: IGroupSummary[] = this.dataChangeSubject.getValue();
          data[updatedIndex] = { ...data[updatedIndex], ComingSoon: false };
          this.dataChangeSubject.next(data);
        }
      } else {
        let data = this.dataChangeSubject.getValue().map((game) => {
          if (game.ComingSoon) {
            const requestTime = DateTime.fromISO(game.RequestTime as string, { zone: 'utc' });
            const startDate = DateTime.fromISO(game.StartDate as string, { zone: 'utc' });
            return { ...game, ComingSoon: game.ComingSoon && startDate > requestTime };
          }
          return game;
        });
        this.dataChangeSubject.next(data);
      }

      return;
    }

    if (event?.completed) {
      let gameGroupItem = this.gamesGroup.find((game) => game.GroupId === event.groupId);
      if (gameGroupItem) {
        gameGroupItem.ComingSoon = false;
        const updatedIndex = this.gamesGroup.indexOf(gameGroupItem);
        this.gamesGroup.splice(updatedIndex, 1, gameGroupItem);
      }
      this.sortGameGroups();
    } else {
      this.gamesGroup.forEach((game) => {
        if (game.ComingSoon) {
          const requestTime = DateTime.fromISO(game.RequestTime as string, { zone: 'utc' });
          const startDate = DateTime.fromISO(game.StartDate as string, { zone: 'utc' });
          game.ComingSoon = game.ComingSoon && startDate > requestTime;
        }
      });
    }
    this.sortGameGroups();
  }

  checkGameEnded(event?: any) {
    if (!this.default) {
      const updatedIndex = this.dataChangeSubject.getValue().findIndex((game) => game.GroupId === event?.groupId);

      if (updatedIndex === -1) return;
      let data = this.dataChangeSubject.getValue();
      const gameGroupItem = data[updatedIndex] as PredictorGroupSummary;
      const now = new Date();
      const endDate = new Date(gameGroupItem.EndDate);

      if (endDate < now && gameGroupItem.UserHasEntered) {
        gameGroupItem.ProcessStatus = PredictorProcessStatus.Processing;
      } else if (endDate < now && !gameGroupItem.UserHasEntered) {
        gameGroupItem.ProcessStatus = PredictorProcessStatus.Failed;
      }
      data[updatedIndex] = gameGroupItem;
      this.dataChangeSubject.next(data);
      return;
    }

    const gameGroupItem = this.gamesGroup.find((game) => game.GroupId === event?.groupId) as PredictorGroupSummary;

    if (!gameGroupItem) return;
    const now = new Date();
    const endDate = new Date(gameGroupItem.EndDate);

    if (endDate < now && gameGroupItem.UserHasEntered) {
      gameGroupItem.ProcessStatus = PredictorProcessStatus.Processing;
    } else if (endDate < now && !gameGroupItem.UserHasEntered) {
      gameGroupItem.ProcessStatus = PredictorProcessStatus.Failed;
    }
    const updatedIndex = this.gamesGroup.findIndex((g) => g.GroupId === gameGroupItem.GroupId);
    if (updatedIndex !== -1) {
      this.gamesGroup.splice(updatedIndex, 1, gameGroupItem);
    }
    this.gamesGroup = [...this.gamesGroup];

    this.hideEndedPredictorGamesGroup();
    this.sortGameGroups();
  }

  sortGameGroups(updatedGroup?: IGroupSummary) {
    if (!this.default) {
      const updatedIndex = this.dataChangeSubject.getValue().findIndex((g) => g.GroupId === updatedGroup?.GroupId);
      if (updatedIndex !== -1) {
        let data: IGroupSummary[] = this.dataChangeSubject.getValue();
        data[updatedIndex] = updatedGroup as IGroupSummary;
        this.dataChangeSubject.next(data);
      }
      return;
    }

    this.sortComingSoonitems();
    if (updatedGroup) {
      const gameGroupItem = this.gamesGroup.find((game) => game.GroupId === updatedGroup.GroupId);
      if (gameGroupItem) {
        const updatedIndex = this.gamesGroup.indexOf(gameGroupItem);
        this.gamesGroup.splice(updatedIndex, 1, updatedGroup);
      }
    }
    this.hideEndedPredictorGamesGroup();
    this.gamesGroup = [
      ...this.gamesGroup.sort((a, b) => {
        if (this.groupType == GroupType.Trivia) {
          const statusOrder = [
            ProgressStatus.InProgress,
            'Featured',
            ProgressStatus.NotStarted,
            'ComingSoon',
            ProgressStatus.Completed,
          ];
          const getStatusIndex = (group: IGroupSummary) => {
            if (group.Locked) {
              return statusOrder.indexOf('Locked');
            }
            if (group.Status === ProgressStatus.NotStarted && group.ComingSoon) {
              return statusOrder.indexOf('ComingSoon');
            }
            if (group.Status === ProgressStatus.NotStarted && group.Featured) {
              return statusOrder.indexOf('Featured');
            }
            return statusOrder.indexOf(group.Status);
          };
          const indexA = getStatusIndex(a);
          const indexB = getStatusIndex(b);

          return indexA - indexB;
        } else {
          const statusOrder = ['resume', 'featured', 'play', 'comingSoon', 'processing', 'completed', 'locked'];
          const getStatusIndex = (group: IGroupSummary) => {
            const predictorGroup = group as PredictorGroupSummary;
            if (predictorGroup.Locked) {
              return statusOrder.indexOf('locked');
            }
            if (predictorGroup.Status === ProgressStatus.NotStarted && predictorGroup.ComingSoon) {
              return statusOrder.indexOf('comingSoon');
            }
            if (
              predictorGroup.Status === ProgressStatus.NotStarted &&
              (predictorGroup as PredictorGroupSummary).ProcessStatus == PredictorProcessStatus.Ready &&
              group.Featured
            ) {
              return statusOrder.indexOf('featured');
            }
            const statusLabel = this.gamesGroupQuery.getPredictorFinalStatus(predictorGroup as PredictorGroupSummary);
            return statusOrder.indexOf(statusLabel.value);
          };
          const indexA = getStatusIndex(a);
          const indexB = getStatusIndex(b);

          return indexA - indexB;
        }
      }),
    ];
  }

  hideEndedPredictorGamesGroup() {
    if (this.groupType == GroupType.Predictor) {
      this.gamesGroup = this.gamesGroup.filter((gameGroupItem) => {
        const predictorGameGroupItem = gameGroupItem as PredictorGroupSummary;

        if (
          (predictorGameGroupItem &&
            predictorGameGroupItem.Status === ProgressStatus.NotStarted &&
            predictorGameGroupItem.ProcessStatus !== PredictorProcessStatus.Ready) ||
          (predictorGameGroupItem && predictorGameGroupItem.ProcessStatus === PredictorProcessStatus.Failed) ||
          (predictorGameGroupItem &&
            this.gamesGroupQuery.checkEndDate(
              String(predictorGameGroupItem.EndDate),
              String(predictorGameGroupItem.RequestTime)
            ) &&
            (predictorGameGroupItem.ProcessStatus == PredictorProcessStatus.Ready ||
              predictorGameGroupItem.ProcessStatus == PredictorProcessStatus.Failed) &&
            predictorGameGroupItem.Status === ProgressStatus.NotStarted)
        ) {
          return false;
        }
        return true;
      });
    }
  }

  sortComingSoonitems() {
    const itemsComingSoon = this.gamesGroup.filter((items) => {
      return items.ComingSoon;
    });
    const listLength = this.gamesGroup.length;
    for (let i = 1; i <= listLength; i++) {
      const index = this.gamesGroup.findIndex((prop) => prop.ComingSoon == true);
      if (index !== -1) {
        this.gamesGroup.splice(index, 1);
      }
    }
    if (itemsComingSoon) {
      itemsComingSoon.sort((a, b) => (a.StartDate > b.StartDate ? 1 : b.StartDate > a.StartDate ? -1 : 0));
      this.gamesGroup.push(...itemsComingSoon);
    }
    return this.gamesGroup;
  }
}
